import Project from "@app/projects/project";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { NetworkIntegrityResultsAsOfOption } from "@app/projects/network-assessment/network-integrity/models/networkIntegrityResultsAsOfOption";
import { ProjectRunConfigurationStatus, ProjectRunConfigurationType } from "@app/projects/enumerators";
import { ResourceType } from "@app/resources";
import { formatDateCustom } from "@app/shared/formatDateTime";

/**
 * Mirrors CountyProjectOverview.canShowNetworkIntegrity. Both must agree: the dropdown may only offer a
 * selectable run that the Network Integrity tab gate also accepts, or selecting it redirects to Summaries.
 */
export function isNetworkIntegrityEligibleRun(runConfiguration: Partial<ProjectRunConfiguration>): boolean {
  if (!runConfiguration) return false;

  return runConfiguration.showNetworkIntegrityDashboard === true
    && runConfiguration.adequacyTemplateCollectionId != null
    && runConfiguration.status === ProjectRunConfigurationStatus.Succeeded
    && !runConfiguration.isTermed
    && !runConfiguration.isProjected
    && !runConfiguration.isProjectedTermed;
}

export function getResultsAsOfLabel(runConfiguration: Partial<ProjectRunConfiguration>): string {
  // Abbreviated month; the remaining parts match moment's LL LT, which this replaced.
  const formatted = formatDateCustom(runConfiguration.dateCreated, "MMM D, YYYY h:mm A");

  if (runConfiguration.isLatestPublished) return `Latest Published (${formatted})`;
  if (!runConfiguration.datePublished) return `Unpublished (${formatted})`;

  return formatted;
}

/**
 * Inclusion is about being a timeslice the user may see: a real run rather than a simulated slice or a companion
 * config, and published unless the caller may see unpublished runs — an unpublished run is withheld outright from
 * everyone else because listing it, even disabled, would disclose that it exists.
 *
 * Whether Network Integrity is actually available on that run is a separate question, and it decides isDisabled
 * rather than membership. Do not hoist any isNetworkIntegrityEligibleRun clause back into the filter: doing so
 * makes the run vanish instead of appearing greyed, which is the behaviour AC 12 exists to provide.
 */
export function getResultsAsOfOptions(
  project: Partial<Project>,
  canSeeUnpublishedRuns: boolean,
): NetworkIntegrityResultsAsOfOption[] {
  // Mirrors Charts.tsx: without the "Show Advanced Comparison Features" project option, only the latest published
  // run is offered. Trending expresses this by dropping OlderPublished items; the equivalent for a run
  // configuration is one that is published but no longer the latest published. Absent the option, restrict —
  // the same direction Charts.tsx takes on a falsy value.
  //
  // Zip projects behave as though the option were always on, by design, which is why none of the zip timeslice
  // UI consults it.
  const isZipProject = project?.resource?.resourceType === ResourceType.ZipAnalysisProject;
  const allowOlderPublishedRuns = isZipProject || !!project?.showAdvancedComparison;

  return (project?.projectRunConfigurations ?? [])
    .filter((rc) =>
      !rc.isTermed
      && !rc.isProjected
      && !rc.isProjectedTermed
      && rc.projectRunConfigurationType !== ProjectRunConfigurationType.DualMembershipSupplemental
      && (!!rc.datePublished || canSeeUnpublishedRuns)
      && (allowOlderPublishedRuns || !rc.datePublished || rc.isLatestPublished))
    .slice()
    .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime())
    .map((rc) => ({
      id: rc.projectRunConfigurationId,
      isDisabled: !isNetworkIntegrityEligibleRun(rc),
      label: getResultsAsOfLabel(rc),
    }));
}
